import { cn } from '@ludus/ui'
import { useTranslation } from 'react-i18next'
import type { MockManager } from '@/shared/mocks/managers.ts'
import { useScheduleSource } from '../../data/useScheduleSource.ts'
import type { ManagerSchedule, ScheduleWindow, Weekday } from '../../types.ts'
import {
  formatWeekdayShort,
  isSameDay,
  isoWeekDates,
  toISODate,
  weekdayFromDate,
} from '../../utils/weekRange.ts'
import { useEditViewState } from '../useEditViewState.ts'
import { DayColumn } from './DayColumn.tsx'
import { HourLabels } from './HourLabels.tsx'
import { useDragSelect, type DragPreview } from './useDragSelect.ts'

interface WeekGridProps {
  manager: MockManager | null
  schedule: ManagerSchedule | null
  visibleDate: Date
}

const COLUMN_MIN = 'min-w-[72px]'

export function WeekGrid({ manager, schedule, visibleDate }: WeekGridProps) {
  const { t } = useTranslation()
  const openEditor = useEditViewState((s) => s.openEditor)
  const dates = isoWeekDates(visibleDate)
  const today = new Date()

  const { upsertException } = useScheduleSource()

  function handleComplete(range: DragPreview) {
    if (!manager) return
    const weekday = weekdayFromDate(new Date(`${range.date}T00:00:00.000Z`)) as Weekday
    openEditor({
      managerId: manager.id,
      date: range.date,
      weekday,
      startMinute: range.startMinute,
      endMinute: range.endMinute,
      workingHours: true,
      scope: 'date',
    })
    void upsertException
  }

  const { preview, startDrag } = useDragSelect(handleComplete)

  function handleTemplateClick(weekday: Weekday, index: number, window: ScheduleWindow) {
    if (!manager) return
    const dayDate = dates.find((d) => weekdayFromDate(d) === weekday) ?? dates[0]
    openEditor({
      managerId: manager.id,
      date: toISODate(dayDate),
      weekday,
      startMinute: window.startMinute,
      endMinute: window.endMinute,
      workingHours: true,
      scope: 'recurring',
      existing: { kind: 'template', weekday, index },
    })
  }

  function handleExceptionClick(date: string, index: number, window: ScheduleWindow) {
    if (!manager) return
    const weekday = weekdayFromDate(new Date(`${date}T00:00:00.000Z`)) as Weekday
    openEditor({
      managerId: manager.id,
      date,
      weekday,
      startMinute: window.startMinute,
      endMinute: window.endMinute,
      workingHours: true,
      scope: 'date',
      existing: { kind: 'exception', date, index },
    })
  }

  function handleOffDayClick(date: string) {
    if (!manager) return
    const weekday = weekdayFromDate(new Date(`${date}T00:00:00.000Z`)) as Weekday
    openEditor({
      managerId: manager.id,
      date,
      weekday,
      startMinute: 9 * 60,
      endMinute: 17 * 60,
      workingHours: false,
      scope: 'date',
      existing: { kind: 'exception', date, index: null },
    })
  }

  if (!manager) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-muted">
        {t('pages.calendar.empty.noManager')}
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col overflow-y-auto" style={{ scrollbarGutter: 'stable' }}>
      <div className="sticky top-0 z-20 flex border-b border-border/60 bg-background-app">
        <div className="w-14 shrink-0" aria-hidden="true" />
        {dates.map((date) => {
          const isToday = isSameDay(date, today)
          return (
            <div
              key={date.toISOString()}
              className={cn(
                'flex flex-1 items-center justify-center gap-2 border-r border-border/60 px-2 py-2 text-xs last:border-r-0',
                COLUMN_MIN,
                isToday ? 'text-foreground' : 'text-muted',
              )}
            >
              <span className="uppercase">{formatWeekdayShort(date)}</span>
              <span
                className={cn(
                  'inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1.5 text-sm tabular-nums',
                  isToday && 'bg-primary text-primary-foreground',
                )}
              >
                {date.getUTCDate()}
              </span>
            </div>
          )
        })}
      </div>

      <div className="flex">
        <HourLabels />
        <div className="relative flex flex-1">
          {dates.map((date) => {
            const dateStr = toISODate(date)
            const weekday = weekdayFromDate(date) as Weekday
            return (
              <DayColumn
                key={dateStr}
                date={dateStr}
                weekday={weekday}
                isToday={isSameDay(date, today)}
                schedule={schedule}
                managerColor={manager.color}
                preview={preview}
                onPointerDown={startDrag}
                onTemplateClick={handleTemplateClick}
                onExceptionClick={handleExceptionClick}
                onOffDayClick={handleOffDayClick}
                className={COLUMN_MIN}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}
